import { useState, useEffect } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import './EnvironmentList.css';

interface ExecutionHistory {
  executionId: string;
  executionArn: string;
  executionName: string;
  status: string;
  startDate: string;
  stopDate?: string;
  requestedBy?: string;
  error?: string;
}

interface Environment {
  executionArn: string;
  executionId: string;
  environmentName: string;
  branch: string;
  status: string;
  startDate: string;
  stopDate?: string;
  ephemeralIdsrvUrl?: string;
  environment?: string;
  requestedBy?: string;
  executionCount: number;
  allExecutions: ExecutionHistory[];
}

function EnvironmentList() {
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useApiClient();

  const loadEnvironments = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/executions');
      setEnvironments(data.executions || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load environments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEnvironments();
  }, []);

  const handleDelete = async (ephemeralCode: string) => {
    if (!confirm(`Are you sure you want to remove environment "${ephemeralCode}" from the list?\n\nNote: This only removes it from the UI. AWS resources will remain active.`)) {
      return;
    }

    try {
      await apiClient.delete(`/executions/${encodeURIComponent(ephemeralCode)}`);
      // Refresh the list
      await loadEnvironments();
    } catch (err: any) {
      setError(`Failed to delete environment: ${err.message}`);
    }
  };

  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running':
        return 'status-running';
      case 'succeeded':
        return 'status-success';
      case 'failed':
      case 'timed_out':
      case 'aborted':
        return 'status-error';
      default:
        return 'status-pending';
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status.toUpperCase()) {
      case 'RUNNING':
        return 'Running';
      case 'SUCCEEDED':
        return 'Completed';
      case 'FAILED':
        return 'Failed';
      case 'TIMED_OUT':
        return 'Timed Out';
      case 'ABORTED':
        return 'Aborted';
      default:
        return status;
    }
  };

  if (loading && environments.length === 0) {
    return (
      <div className="environment-list">
        <h2>Active Environments</h2>
        <div className="loading">Loading environments...</div>
      </div>
    );
  }

  return (
    <div className="environment-list">
      <div className="list-header">
        <h2>Active Environments</h2>
        <button onClick={loadEnvironments} className="refresh-button" disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {environments.length === 0 ? (
        <div className="empty-state">
          <p>No active environments found.</p>
          <p>Create a new environment to get started.</p>
        </div>
      ) : (
        <div className="environment-grid">
          {environments.map((env) => (
            <div key={env.executionArn} className="environment-card">
              <div className="card-header">
                <h3>{env.environmentName}</h3>
                <span className={`status-badge ${getStatusClass(env.status)}`}>
                  {getStatusDisplay(env.status)}
                </span>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="label">Target Env:</span>
                  <span className="value">{env.environment || env.branch}</span>
                </div>
                {env.ephemeralIdsrvUrl && (
                  <div className="info-row">
                    <span className="label">Identity URL:</span>
                    <span className="value url-value">
                      <a href={env.ephemeralIdsrvUrl} target="_blank" rel="noopener noreferrer">
                        {env.ephemeralIdsrvUrl}
                      </a>
                    </span>
                  </div>
                )}
                <div className="info-row">
                  <span className="label">Started:</span>
                  <span className="value">{new Date(env.startDate).toLocaleString()}</span>
                </div>
                {env.stopDate && (
                  <div className="info-row">
                    <span className="label">Completed:</span>
                    <span className="value">{new Date(env.stopDate).toLocaleString()}</span>
                  </div>
                )}
                {env.requestedBy && (
                  <div className="info-row">
                    <span className="label">Requested By:</span>
                    <span className="value">{env.requestedBy}</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="label">Total Runs:</span>
                  <span className="value">{env.executionCount}</span>
                </div>
              </div>
              <div className="card-actions">
                <button
                  onClick={() => handleDelete(env.environmentName)}
                  className="delete-button"
                  title="Remove from list (does not delete AWS resources)"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EnvironmentList;
